package com.schoolconnect.app.feature.homework

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppSuccessSoft
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.util.decodeImageBitmap
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.download
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun ViewHomeworkDialog(
    homework: HomeworkRowUi?,
    attachmentMetadata: Map<String, HomeworkAttachmentMetadataUi> = emptyMap(),
    isLoading: Boolean = false,
    errorMessage: String = "",
    showHomeworkId: Boolean = true,
    onDismiss: () -> Unit
) {
    if (homework == null) return

    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Standard,
        maxDialogHeight = 820.dp,
        isLoading = isLoading,
        loadingTitle = "Loading homework"
    ) {
        AppDialogHeader(
            title = "View Homework",
            titleSuffix = homework.homeworkId.takeIf { showHomeworkId },
            subtitle = "Homework details and activity.",
            onClose = onDismiss,
            colors = AppDialogDefaultColors
        )

        AppDialogBody(
            horizontalPadding = 20.dp,
            compactHorizontalPadding = 16.dp,
            verticalPadding = 12.dp,
            colors = AppDialogDefaultColors
        ) { _ ->
            if (errorMessage.isNotBlank()) {
                AppDialogErrorMessage(errorMessage)
            }
            HomeworkAuditCard(homework)
            HomeworkInformationCard(homework, attachmentMetadata, showHomeworkId)
        }

        AppDialogFooter(
            actions = listOf(AppDialogFooterAction("Close", onDismiss)),
            footerHeight = 64.dp
        )
    }
}

@Composable
private fun HomeworkAuditCard(homework: HomeworkRowUi) {
    AppCard(
        modifier = Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)),
        containerColor = Color.White
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(14.dp),
            horizontalArrangement = Arrangement.spacedBy(18.dp)
        ) {
            AuditPerson("Created By", homework.assignedBy, homework.assignedDate, Modifier.weight(1f))
            Surface(modifier = Modifier.width(1.dp).height(58.dp), color = AppBorder) {}
            AuditPerson("Last Updated", "Not provided", homework.updatedDate.ifBlank { homework.assignedDate }, Modifier.weight(1f))
        }
    }
}

@Composable
private fun AuditPerson(label: String, name: String, date: String, modifier: Modifier) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(5.dp)) {
        Text(label, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Row(horizontalArrangement = Arrangement.spacedBy(7.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(painterResource(Res.drawable.user), null, tint = AppNavy, modifier = Modifier.size(19.dp))
            Text(name, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.Medium)
        }
        Text(date, color = AppDialogDefaultColors.muted, fontSize = 11.sp)
    }
}

@Composable
private fun HomeworkInformationCard(
    homework: HomeworkRowUi,
    attachmentMetadata: Map<String, HomeworkAttachmentMetadataUi>,
    showHomeworkId: Boolean
) {
    val uriHandler = LocalUriHandler.current
    val attachments = homework.attachmentFileUrls.mapIndexed { index, url ->
        HomeworkViewAttachment(
            url = url,
            position = index + 1,
            metadata = homework.attachmentFileIds.getOrNull(index)?.let(attachmentMetadata::get)
        )
    }.ifEmpty {
        homework.attachmentFileIds.mapIndexed { index, fileId ->
            HomeworkViewAttachment(url = "", position = index + 1, metadata = attachmentMetadata[fileId])
        }
    }

    AppCard(
        modifier = Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)),
        containerColor = Color.White
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(9.dp)) {
                Surface(color = AppDialogDefaultColors.iconTile, shape = RoundedCornerShape(9.dp)) {
                    Icon(painterResource(Res.drawable.document), null, tint = AppPrimary, modifier = Modifier.padding(7.dp).size(20.dp))
                }
                AppDialogSectionLabel("Homework Information", fontSize = 16.sp)
            }
            Spacer(Modifier.height(2.dp))

            if (showHomeworkId) HomeworkInfoRow("Homework ID", homework.homeworkId)
            HomeworkInfoRow("Homework Title", homework.title)
            HomeworkInfoRow("Class", homework.className)
            HomeworkInfoRow("Subject", homework.subject)
            HomeworkInfoRow("Assigned By", homework.assignedBy)
            HomeworkInfoRow("Assigned Date", homework.assignedDate)
            HomeworkInfoRow("Due Date", homework.dueDate)
            HomeworkInfoRow("Status", homework.status, badge = true)
            HomeworkInfoRow("Notify Parents", homework.notifyParents?.let { if (it) "Yes" else "No" } ?: "Not provided")

            AppDialogDivider()
            Text("Homework Description", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = AppDialogDefaultColors.surface,
                shape = RoundedCornerShape(8.dp),
                border = BorderStroke(1.dp, AppBorder)
            ) {
                Text(
                    text = homework.description.ifBlank { "—" },
                    color = AppNavy,
                    fontSize = 12.sp,
                    lineHeight = 18.sp,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp)
                )
            }

            Text("Attachment", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            if (attachments.isEmpty()) {
                AttachmentEmptyState()
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    attachments.forEach { attachment ->
                        HomeworkAttachmentCard(
                            attachment = attachment,
                            onDownload = { attachment.url.takeIf { it.isNotBlank() }?.let(uriHandler::openUri) }
                        )
                    }
                }
            }
        }
    }
}

private data class HomeworkViewAttachment(
    val url: String,
    val position: Int,
    val metadata: HomeworkAttachmentMetadataUi? = null
) {
    val name: String get() = metadata?.name ?: "Attachment $position"
    val sizeLabel: String get() = metadata?.sizeLabel ?: "Size unavailable"
    val isImage: Boolean get() = metadata?.isImage == true
    val type: String
        get() {
            metadata?.type?.takeIf { it.isNotBlank() }?.let { return it }
            val path = url.substringBefore('?').substringAfterLast('/')
            return path.substringAfterLast('.', "").takeIf { it.isNotBlank() }?.uppercase() ?: "FILE"
        }
}

@Composable
private fun AttachmentEmptyState() {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = AppDialogDefaultColors.surface,
        shape = RoundedCornerShape(8.dp),
        border = BorderStroke(1.dp, AppBorder)
    ) {
        Text("No attachment", color = AppDialogDefaultColors.muted, fontSize = 12.sp, modifier = Modifier.padding(12.dp))
    }
}

@Composable
private fun HomeworkAttachmentCard(
    attachment: HomeworkViewAttachment,
    onDownload: () -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = AppDialogDefaultColors.surface,
        shape = RoundedCornerShape(8.dp),
        border = BorderStroke(1.dp, AppBorder)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            if (attachment.isImage && attachment.metadata?.previewBytes != null) {
                val image = rememberAttachmentPreview(attachment.metadata.fileId, attachment.metadata.previewBytes)
                if (image != null) {
                    Image(
                        bitmap = image,
                        contentDescription = attachment.name,
                        modifier = Modifier.size(96.dp),
                        contentScale = ContentScale.Fit
                    )
                }
            } else {
                Surface(
                    modifier = Modifier.size(96.dp),
                    color = AppDialogDefaultColors.iconTile,
                    shape = RoundedCornerShape(6.dp)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(painterResource(Res.drawable.document), null, tint = AppNavy, modifier = Modifier.size(23.dp))
                    }
                }
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(attachment.name, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Medium, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text("${attachment.type}  •  ${attachment.sizeLabel}", color = AppDialogDefaultColors.muted, fontSize = 11.sp)
            }
            if (attachment.url.isNotBlank()) {
                AppOutlinedButton(onClick = onDownload, modifier = Modifier.height(36.dp)) {
                    Icon(painterResource(Res.drawable.download), "Download ${attachment.name}", tint = AppPrimary, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text("Download", color = AppPrimary, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun rememberAttachmentPreview(fileId: String, bytes: ByteArray): ImageBitmap? =
    remember(fileId, bytes) { decodeImageBitmap(bytes) }

@Composable
private fun HomeworkInfoRow(
    label: String,
    value: String,
    badge: Boolean = false
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(label, color = AppNavy, fontSize = 12.sp, modifier = Modifier.width(150.dp))
        Text(":", color = AppDialogDefaultColors.muted, fontSize = 12.sp, modifier = Modifier.width(22.dp))
        if (badge) {
            val isPositive = value == "Open" || value == "Enabled"
            Surface(
                color = if (isPositive) AppSuccessSoft else Color(0xFFF3F4F7),
                shape = RoundedCornerShape(6.dp)
            ) {
                Text(
                    value,
                    color = if (isPositive) AppSuccess else AppNavy,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(horizontal = 9.dp, vertical = 5.dp)
                )
            }
        } else {
            Text(value, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        }
    }
}
